import { useCallback, useMemo } from "react";
import { TellescopeModelFromName, useAuth, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import {
  CONTACT_DETAILS_VIEW_TYPE,
  DEFAULT_CONTACT_DETAILS_VIEW_ID,
  getDefaultView,
  isFetchableViewId,
} from "../components/ContactDetailsHeader/constants";

export interface UseContactDetailsViewsArgs {
  selectedViewId: string | undefined;
}

/**
 * Loads the contact-details views the current user can see, and resolves which one is actually
 * selected.
 *
 * Resolution happens during render and is pure: nothing is written back to the caller's state. The
 * caller keeps persisting only what the user explicitly picked, and passes the returned `viewId` to
 * **both** the header and the body — they must agree, or the header's dropdown and the rendered
 * blocks describe different views.
 *
 * ## Visibility
 *
 * A view is listed when the user created it, is named in its `userIds`, or it is `visibleToAllUsers`
 * — and is then dropped again if the user holds a role in its `hiddenFromRoles`. Exclusion wins over
 * sharing, which is what lets a view be shared broadly while still being withheld from some roles.
 * The creator is exempt from their own view's exclusions, so configuring a view cannot hide it from
 * the person editing it. Because this list also feeds auto-selection below, filtering is what keeps a
 * hidden view from being silently selected and rendered.
 *
 * That predicate runs **server-side**, as the `mdbFilter` on the read: filtering client-side would
 * page against unfiltered results, so a user whose org holds more than `limit` contact-details views
 * could have the ones they can actually see pushed past the fetch window and see none of them. The
 * creator's exemption is the shape of the `$or` rather than a separate check — the creator branch
 * never reaches the `hiddenFromRoles` test. `$nin` against an array field means "no intersection",
 * and for a user with no roles it matches every view, which is correct: they are excluded by nothing.
 *
 * `select` still drops any record whose `type` is not {@link CONTACT_DETAILS_VIEW_TYPE}. `mdbFilter`
 * is untyped (`any`) at the SDK boundary, and `type` is optional on the model, so this is the one
 * check worth keeping locally — a typo in the filter that widened the read would otherwise let this
 * feature append blocks onto another feature's dashboard.
 *
 * ## Defaults
 *
 * With no explicit selection, resolution lands on the view configured as the user's default, falling
 * back to the first visible view. `defaultForUserIds` (named for them specifically) outranks
 * `defaultForRoles` (named for a role they hold), so the more specific setting wins.
 *
 * Defaults only supply that fallback — an explicit pick still wins. Which is why callers should route
 * selections through the returned `resolveSelection`: it drops a pick that already matches the
 * fallback, so picking your own default leaves the store empty and you keep following the default
 * rather than being pinned to today's value of it.
 *
 * Two cases make the persisted selection differ from the resolved one:
 *
 * - **Nothing picked yet.** A fresh session has no selection. Landing on the synthesized
 *   placeholder when real saved views exist is the wrong default, so the first real view is
 *   selected instead. The placeholder is only resolved to when there is no real view to pick.
 * - **The selection no longer exists.** Selections are persisted, so an id can outlive the view it
 *   names — deleted by a teammate, or belonging to an org the user has lost access to. There is no
 *   local event for either, so the only signal is the id being absent from the visible list. Left
 *   uncorrected, the body would query a record that never resolves and sit on its loading state.
 *
 * Deletions performed by *this* user are an observable action and are handled at the event that
 * causes them, not here.
 *
 * A failed read reports `isError` and resolves `viewId` to `undefined` rather than falling through to
 * the placeholder. react-query reports a failure as `isLoading: false` with `data: undefined`, which
 * is indistinguishable from "this user has no saved views" — and resolving to the placeholder there
 * means the next block-add materializes a *second* view, orphaning the real one the read failed to
 * load. Callers must gate the body on `viewId`, as the example does.
 */
export const useContactDetailsViews = ({ selectedViewId }: UseContactDetailsViewsArgs) => {
  const { currentUser } = useAuth();
  const currentUserRoles = useMemo(() => currentUser.roles ?? [], [currentUser.roles]);

  const {
    data: views = [],
    isLoading,
    isError,
  } = useTellescopeInfiniteQuery({
    model: "custom_dashboards",
    method: "getSome",
    request: {
      limit: 100,
      mdbFilter: {
        type: CONTACT_DETAILS_VIEW_TYPE,
        $or: [
          { creator: currentUser.id },
          {
            $and: [
              { $or: [{ userIds: currentUser.id }, { visibleToAllUsers: true }] },
              { hiddenFromRoles: { $nin: currentUserRoles } },
            ],
          },
        ],
      },
    },
    fetchAllPages: true,
    select: (data): TellescopeModelFromName<"custom_dashboards">[] => {
      const contactDetailsViews = data.pages.flat().filter((view) => view.type === CONTACT_DETAILS_VIEW_TYPE);

      if (!contactDetailsViews.length) {
        return [getDefaultView()];
      }

      return contactDetailsViews;
    },
  });

  const defaultViewId = useMemo(() => {
    const realViews = views.filter((view) => isFetchableViewId(view.id));

    return (
      realViews.find((view) => view.defaultForUserIds?.includes(currentUser.id))?.id ??
      realViews.find((view) => view.defaultForRoles?.some((role) => currentUserRoles.includes(role)))?.id
    );
  }, [currentUser.id, currentUserRoles, views]);

  const autoSelectedViewId = useMemo(() => {
    const firstRealViewId = views.find((view) => isFetchableViewId(view.id))?.id;
    return defaultViewId ?? firstRealViewId ?? DEFAULT_CONTACT_DETAILS_VIEW_ID;
  }, [defaultViewId, views]);

  const viewId = useMemo(() => {
    if (isError) return undefined;
    if (isLoading) return selectedViewId;
    if (!isFetchableViewId(selectedViewId)) return autoSelectedViewId;

    return views.some((view) => view.id === selectedViewId) ? selectedViewId : autoSelectedViewId;
  }, [autoSelectedViewId, isError, isLoading, selectedViewId, views]);

  const resolveSelection = useCallback(
    (pickedViewId: string | undefined) => (pickedViewId === autoSelectedViewId ? undefined : pickedViewId),
    [autoSelectedViewId]
  );

  return { views, isLoading, isError, viewId, defaultViewId, resolveSelection };
};
